import { AxiosInstance } from 'axios';
import { RefreshTokenData, TokenData } from '@/features/auth/types';
import { removeLiveChat } from '@/features/common/plugins/liveChat';

export const routesAvailableWithoutAuth = [
  'sign-in',
  'logout',
  'verify-user',
  'verify-password',
  'password',
  'account-disabled',
  'account-deleted',
];

export const saveToken = ({ token, expire, refreshToken }: TokenData): void => {
  localStorage.setItem('token', JSON.stringify(token));
  localStorage.setItem('tokenExpireDate', JSON.stringify(expire));
  localStorage.setItem('refreshToken', JSON.stringify(refreshToken));
};

export const getToken = (): string => {
  const item = localStorage.getItem('token');
  if (!item) return '';
  return JSON.parse(item || '');
};

export const getRefreshToken = (): string => {
  const item = localStorage.getItem('refreshToken');
  if (!item) return '';
  return JSON.parse(item || '');
};

export const getTokenExpireDate = (): string => {
  const item = localStorage.getItem('tokenExpireDate');
  if (!item) return '';
  return JSON.parse(item || '');
};

export const getIsExpiredToken = (): boolean => {
  const expiryDate = getTokenExpireDate();
  if (!expiryDate) return true;

  const tokenExpireDate = new Date(expiryDate as string);
  const today = new Date();

  return tokenExpireDate < today;
};

const TOKEN_REFRESH_BUFFER_MS = 30_000;

export const getIsTokenExpiredOrNearExpiry = (
  bufferMs: number = TOKEN_REFRESH_BUFFER_MS,
): boolean => {
  const expiryDate = getTokenExpireDate();
  if (!expiryDate) return true;
  const tokenExpireDate = new Date(expiryDate as string);
  const now = new Date();
  return tokenExpireDate.getTime() - now.getTime() <= bufferMs;
};

export const getTokenData = (): TokenData => {
  return {
    token: getToken(),
    expire: getTokenExpireDate(),
    refreshToken: getRefreshToken(),
  };
};

export const refreshToken = async (service: AxiosInstance): Promise<boolean> => {
  const payload: RefreshTokenData = {
    token: getToken(),
    refreshToken: getRefreshToken(),
  };

  if (!payload.token || !payload.refreshToken) return false;

  const response = await service.post('/auth/refresh-token', payload);
  saveToken(response.data as TokenData);

  return true;
};

export const getTokenAsQuery = async (service: AxiosInstance): Promise<string> => {
  if (getIsExpiredToken()) {
    await refreshToken(service);
  }
  return getToken();
};

export const clearToken = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('tokenExpireDate');
  localStorage.removeItem('refreshToken');
  removeLiveChat();
  localStorage.setItem('auth_event',
    JSON.stringify({
      type: 'logout',
      timestamp: Date.now(),
    }));
};

export const saveLastLoginEmail = (email: string): void => {
  localStorage.setItem('lastLoginEmail', email);
};

export const getLastLoginEmail = (): string | null => {
  return localStorage.getItem('lastLoginEmail');
};

export const deleteLastLoginEmail = (): void => {
  localStorage.removeItem('lastLoginEmail');
};

export const saveCurrentLoggedInUser = (user: { email: string }): void => {
  localStorage.setItem('currentLoggedInUser', JSON.stringify(user));
};

export const getCurrentLoggedInUser = (): { email: string } | null => {
  const user = localStorage.getItem('currentLoggedInUser');
  if (!user) return null;
  return JSON.parse(user);
};

export const deleteCurrentLoggedInUser = (): void => {
  localStorage.removeItem('currentLoggedInUser');
};

export function validatePasswordRequirements(password: string): {
  label: string;
  met: boolean;
}[] {
  const requirements = [
    { label: '8 or more characters', met: password.length >= 8 },
    {
      label: 'Upper & lowercase letters',
      met: /[A-Z]/.test(password) && /[a-z]/.test(password),
    },
    { label: 'At least one number', met: /[0-9]/.test(password) },
    {
      label: 'At least one symbol',
      met: /[!@#$%^&*()\-=_+[\]{}|;':",.<>/?]/.test(password),
    },
  ];
  return requirements;
}
